/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataSourceAliasProp } from 'componentregistry';
import { pageAtom } from 'designer/atoms';
import { helpTextSelector } from 'designer/state';
import { camelCase } from 'lodash-es';
import React, { memo, useCallback, useState } from 'react';
import { getAllDataSources } from './DataSourceAliasChooserProperty';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
});

function DataSourceAliasProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value: alias = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<DataSourceAliasProp, string>) {
  const classes = useStyles();
  const [value, setValue] = useState(alias);
  const [errorMsg, setErrorMessage] = useState('');
  React.useEffect(() => {
    setValue(alias);
  }, [alias]);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  const isValid = useCallback(
    (val: string): boolean => {
      if (val && alias !== val) {
        const items = getAllDataSources(platform, appUid, pageId);
        const ds = items.find((itm) => itm.props.alias === val);
        if (ds) {
          setErrorMessage(`Duplicate alias [${val}]!`);
          return false;
        }
        setErrorMessage('');
        return true;
      }
      setErrorMessage('');
      return true;
    },
    [alias, appUid, pageId, platform],
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    isValid(val);
    setValue(val);
  }
  function onBlur() {
    const val = camelCase(value ?? '');
    if (!value) {
      setValue(alias);
    } else if (alias !== val && isValid(val)) {
      onChange(property, val);
      let page = platform.getter(pageAtom, { params: { appUid, pageId } });
      if (page && alias && val) {
        page = { ...page };
        page = platform.replacePropertyValue(
          page,
          appUid,
          pageId,
          'DataSourceAliasChooser',
          alias,
          val,
        );
        platform.setter(pageAtom, page, { params: { appUid, pageId } });
      }
      platform.resetAliases(appUid, pageId);
    }
  }

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <TextField
      disabled={isViewOnlyUser}
      data-testid={property}
      inputProps={{ spellCheck: 'false' }}
      variant="standard"
      className={classes.root}
      classes={{ root: classes.input }}
      helperText={errorMsg || helpText}
      fullWidth
      label={def.label}
      onBlur={onBlur}
      onChange={handleChange}
      required={required}
      value={value ?? ''}
      onFocus={(e) => {
        e.target.select();
      }}
      error={!!errorMsg}
    />
  );
}

export default memo(DataSourceAliasProperty);
